import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { PartyFieldLabel, PartyFormSection } from "@/components/parties/PartyFormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  brokerErrorMessage,
  createBroker,
  emptyBrokerDraft,
  isBrokerDraftComplete,
  validateBrokerDraft,
  type BrokerDraft,
  type BrokerRecord,
} from "@/lib/brokers/brokersStore";
import { getStoredAuthUser } from "@/lib/authUser";
import { formatWithMask } from "@/lib/utils";
import { Handshake, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: BrokerRecord) => void | Promise<void>;
  title?: string;
};

export function CreateBrokerDialog({
  open,
  onOpenChange,
  onCreated,
  title = "Add broker",
}: Props) {
  const [draft, setDraft] = useState<BrokerDraft>(() => emptyBrokerDraft());
  const [saving, setSaving] = useState(false);
  const user = getStoredAuthUser();
  const mask = user?.company_branding?.cnic_masking;
  const cnicPlaceholder = mask ? mask.replace(/9/g, "x") : "xxxxx-xxxxxxx-x";
  const cnicMaxLength = mask ? mask.length : 32;

  useEffect(() => {
    if (!open) return;
    setDraft(emptyBrokerDraft());
    setSaving(false);
  }, [open]);

  const update = <K extends keyof BrokerDraft>(key: K, value: BrokerDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const save = async () => {
    if (saving) return;
    const validation = validateBrokerDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete required broker fields.");
      return;
    }
    setSaving(true);
    try {
      const { record, message } = await createBroker(draft);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(brokerErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartyUpsertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="Add a sale broker / agent. They are saved in parties and can be selected on sales."
      icon={Handshake}
      accent="emerald"
      contentClassName="!max-w-3xl w-[min(95vw,48rem)] p-6 sm:p-8"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || !isBrokerDraftComplete(draft)}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save broker
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <PartyFormSection title="Broker details" contentClassName="!grid-cols-1 sm:!grid-cols-2 lg:!grid-cols-2 gap-x-5 gap-y-5">
          <div className="space-y-2 min-w-0">
            <PartyFieldLabel required>Broker name</PartyFieldLabel>
            <Input
              className="glass-input h-11 text-sm w-full"
              value={draft.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. Ali Broker"
              disabled={saving}
            />
          </div>
          <div className="space-y-2 min-w-0">
            <PartyFieldLabel required>Contact number</PartyFieldLabel>
            <Input
              className="glass-input h-11 text-sm w-full"
              value={draft.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="03xxxxxxxxx"
              disabled={saving}
            />
          </div>
          <div className="space-y-2 min-w-0">
            <PartyFieldLabel>Father name</PartyFieldLabel>
            <Input
              className="glass-input h-11 text-sm w-full"
              value={draft.fatherName ?? ""}
              onChange={(e) => update("fatherName", e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-2 min-w-0">
            <PartyFieldLabel>CNIC</PartyFieldLabel>
            <Input
              className="glass-input h-11 text-sm w-full"
              value={draft.cnic ?? ""}
              onChange={(e) =>
                update("cnic", mask ? formatWithMask(e.target.value, mask) : e.target.value)
              }
              placeholder={cnicPlaceholder}
              maxLength={cnicMaxLength}
              disabled={saving}
            />
          </div>
        </PartyFormSection>
      </div>
    </PartyUpsertDialog>
  );
}
